import axios, { AxiosError, AxiosRequestConfig } from "axios";
import store from "../redux/store";
import { setLoading } from "../redux/loadingSlice";
import { toast } from "react-toastify";
import { login, logout } from "../redux/authSlice";
import { CONFIG } from "../config";
import { isTokenExpiringBeyond8Days } from "./Auth";
import getStrings from "../localization/getStrings";
import {
	createSessionRefresher,
	isTokenExpiringWithin,
	looksLikeJwt,
	sleep,
} from "./sessionRefresh";

const URL = `${CONFIG?.BASE_URL}/api`;

declare module "axios" {
	export interface AxiosRequestConfig {
		/**
		 * Skip the full-screen loader and the error toast. For requests whose screen shows its
		 * own loading and error state, such as background refreshes of cached data.
		 */
		silent?: boolean;
	}
}

/** Per attempt. With no limit, one hung refresh kept every request queued behind it hanging too. */
const REFRESH_TIMEOUT_MS = 15 * 1000;

const session = createSessionRefresher({
	request: () =>
		axios
			.get(`${URL}/user/refresh-token`, {
				withCredentials: true,
				timeout: REFRESH_TIMEOUT_MS,
			})
			.then((res) => {
				if (!looksLikeJwt(res.data)) {
					// e.g. a captive portal or proxy answering 200: not a token, and not the API saying no
					throw new Error(
						"The refresh-token response is not a token"
					);
				}
				return res.data; // the new JWT
			}),
	onRefreshed: (token) => {
		localStorage.setItem("token", token);
		store.dispatch(login({ token }));
	},
	onSessionEnded: () => store.dispatch(logout()),
	sleep,
});

const REFRESH_FAILED_TOAST_ID = "session-refresh-failed";

/** Tells the user a request failed because the session could not be renewed just now. */
function notifyRefreshFailed() {
	const { lang } = store.getState().langReducer;
	const strings = getStrings("session", lang) as
		| { refreshFailed?: string }
		| undefined;
	toast.error(
		strings?.refreshFailed ??
			"Could not reach the server. Please try again in a moment.",
		{ toastId: REFRESH_FAILED_TOAST_ID } // one toast, however many requests failed
	);
}

// --------------------------------------------------
// instance
// --------------------------------------------------
const api = axios.create({
	baseURL: URL,
	timeout: 9000 * 1000,
	withCredentials: true,
});
api.defaults.timeout = 9000 * 1000;
api.defaults.withCredentials = true;

// ---------------- request interceptor ----------------
api.interceptors.request.use(async (config) => {
	const token = localStorage.getItem("token");
	if (token) {
		config.headers.Authorization = `Bearer ${token}`;
	}
	if (!config.silent) {
		store.dispatch(setLoading({ isLoading: true }));
	}

	// OPTIONAL: proactive refresh 30 s before expiry
	if (
		token &&
		(isTokenExpiringWithin(token, 30) || isTokenExpiringBeyond8Days())
	) {
		try {
			const newToken = await session.refresh();
			config.headers.Authorization = `Bearer ${newToken}`;
		} catch (err) {
			// Logged out, or the current token has already expired: this request cannot succeed.
			if (session.endedSession(err) || isTokenExpiringWithin(token, 0)) {
				if (!session.endedSession(err) && !config.silent) {
					notifyRefreshFailed();
				}
				return Promise.reject(err);
			}
			// The refresh failed for a reason that may pass and the current token is still valid
			// for a few seconds: send the request with it. The next request tries the refresh again.
		}
	}

	return config;
});

// ---------------- response interceptor ----------------
api.interceptors.response.use(
	(res) => {
		if (!res.config.silent) {
			store.dispatch(setLoading({ isLoading: false }));
		}
		return res;
	},
	async (error: AxiosError) => {
		if (!error.config?.silent) {
			store.dispatch(setLoading({ isLoading: false }));
		}

		if (
			!error.config?.silent &&
			!error.config?.url?.includes("/user/refresh-token") &&
			error.response?.data
		) {
			const data = error.response.data as unknown;
			const body = data as {
				detail?: string;
				title?: string;
				message?: string;
			};
			// detail/title = ProblemDetails; message = API error DTOs (e.g. bulk order operations)
			const message =
				typeof data === "string"
					? data
					: body?.detail ??
					  body?.title ??
					  body?.message ??
					  "Something went wrong";
			toast.error(message);
		}

		// short–circuit if it's the refresh call itself: a failed proactive refresh rejected by the
		// request interceptor. The session refresher has already logged out, or kept the session.
		if (error.config?.url?.includes("/user/refresh-token")) {
			return Promise.reject(error);
		}

		const originalRequest = error.config as AxiosRequestConfig & {
			_retry?: boolean;
		};

		// plain network error or non‑401 – just bubble up
		if (!error.response || error.response.status !== 401) {
			return Promise.reject(error);
		}

		if (originalRequest._retry) {
			return Promise.reject(error);
		}

		originalRequest._retry = true;

		try {
			const newToken = await session.refresh();

			originalRequest.headers = {
				...originalRequest.headers,
				Authorization: `Bearer ${newToken}`,
			};
			return api(originalRequest);
		} catch (refreshErr: any) {
			// Unless it logged the user out, the refresh failed for a reason that may pass: keep the
			// user, and whatever they were doing, on the page. Their next action tries again.
			if (!session.endedSession(refreshErr) && !originalRequest.silent) {
				notifyRefreshFailed();
			}
			return Promise.reject(refreshErr);
		}
	}
);

export default api;
